/**
 * FlowPathNameTags — the (mandatory) path name plus its flow tags on one row.
 *
 * The name always wins the space. When the full chips fit they render inline as
 * "{emoji|⚡} NAME"; when they would push or clip the name they collapse to the
 * compact form — each tag shown as just its emoji, or a tag-coloured dot when it
 * has none (hover lists the names). A hidden probe measures the natural chip
 * width so the decision never truncates the name to show a tag.
 * For Media Devoted | Created By Ciro Autiero
 */

import { CSSProperties, FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import { useFlowTagsFor } from '@/features/flow-optimizer/components/preview/flow-tags-context';

const CHIP_CLASS =
  'inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full border px-1 text-[9px] font-semibold leading-none';

/** Tinted chip style from a tag colour (transparent-ish bg + matching border). */
function chipStyle(color?: string | null): CSSProperties | undefined {
  return color ? { backgroundColor: `${color}22`, color, borderColor: `${color}55` } : undefined;
}

/** True when the name + the full chip set can't share the row (tags must collapse). */
export function tagsOverflow(
  nameWidth: number,
  chipsWidth: number,
  availWidth: number,
  gap = 8,
): boolean {
  if (chipsWidth <= 0) return false;
  return nameWidth + gap + chipsWidth > availWidth;
}

interface TagVisual {
  tag: string;
  color?: string | null;
  emoji?: string | null;
}

/** Full form: "{emoji|⚡} NAME" tinted by the tag colour. */
const FlowTagChip: FC<TagVisual> = ({ tag, color, emoji }) => (
  <span className={CHIP_CLASS} style={chipStyle(color)}>
    <span aria-hidden>{emoji || '⚡'}</span>
    {tag}
  </span>
);

/** Compact form: the emoji alone, or a tag-coloured dot when there's no emoji. */
const FlowTagMarker: FC<TagVisual> = ({ tag, color, emoji }) =>
  emoji ? (
    <span className="text-[11px] leading-none" role="img" aria-label={tag} title={tag}>
      {emoji}
    </span>
  ) : (
    <span
      className="inline-block h-1.5 w-1.5 rounded-full"
      style={{ backgroundColor: color ?? 'currentColor' }}
      role="img"
      aria-label={tag}
      title={tag}
    />
  );

export interface FlowPathNameTagsProps {
  name: string;
  nameClassName?: string;
  layer: FlowLayer;
  itemId: string;
  pathId?: string;
  excluded?: ReactNode;
}

export const FlowPathNameTags: FC<FlowPathNameTagsProps> = ({
  name,
  nameClassName,
  layer,
  itemId,
  pathId,
  excluded,
}) => {
  const { tags, colorOf, emojiOf } = useFlowTagsFor(layer, itemId, pathId);
  const rowRef = useRef<HTMLSpanElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const tagKey = tags.join('|');

  useLayoutEffect(() => {
    const row = rowRef.current;
    const nm = nameRef.current;
    const probe = probeRef.current;
    if (!row || !nm || !probe || tags.length === 0) return;
    const recompute = () =>
      setCollapsed(tagsOverflow(nm.scrollWidth, probe.scrollWidth, row.clientWidth));
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(row);
    return () => ro.disconnect();
  }, [tagKey]);

  const visuals: TagVisual[] = tags.map((tag) => ({
    tag,
    color: colorOf(tag),
    emoji: emojiOf(tag),
  }));

  return (
    <span ref={rowRef} className="relative flex min-w-0 items-center gap-1">
      <span ref={nameRef} className={nameClassName} title={name}>
        {name}
      </span>
      {excluded}
      {visuals.length > 0 && (
        <>
          {/* Hidden natural-width probe for the full chip set (never painted). */}
          <span
            ref={probeRef}
            aria-hidden
            className="pointer-events-none invisible absolute left-0 top-0 inline-flex items-center gap-0.5"
          >
            {visuals.map((v) => (
              <FlowTagChip key={v.tag} {...v} />
            ))}
          </span>
          {collapsed ? (
            <span
              className="inline-flex shrink-0 items-center gap-1"
              title={`Flow tags: ${tags.join(', ')}`}
            >
              {visuals.map((v) => (
                <FlowTagMarker key={v.tag} {...v} />
              ))}
            </span>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-0.5">
              {visuals.map((v) => (
                <FlowTagChip key={v.tag} {...v} />
              ))}
            </span>
          )}
        </>
      )}
    </span>
  );
};

export default FlowPathNameTags;
